import {
  isFixedRateLoanProductTemplate,
  isFloatingRateLoanProductTemplate,
  isInstantiatedFixedRateLoanSecurity,
  isInstantiatedFloatingRateLoanSecurity,
  isInstantiatedOTCForwardSecurity,
  isInstantiatedOTCOptionSecurity,
  isInstantiatedOTCRepoSecurity,
  isOTCForwardProductTemplate,
  isOTCOptionProductTemplate,
  isOTCRepoProductTemplate,
  type OTCFixedRateLoanSecurity,
  type OTCFloatingRateLoanSecurity,
  type OTCForwardSecurity,
  type OTCOptionSecurity,
  type OTCRepoSecurity,
  type Security,
} from '@talos/kyoko';

/**
 * Single source for which form field represents the active booking context:
 * - **Modifying** an existing deal → `symbolField` (instrument / instantiated row).
 * - **New** booking → `templateSymbolField` (product template).
 */
export function securityForManualDealMode(
  form: {
    symbolField?: { value?: Security };
    templateSymbolField?: { value?: Security };
  },
  isModifying: boolean
): Security | undefined {
  return isModifying ? form.symbolField?.value : form.templateSymbolField?.value;
}

/**
 * Active symbol string for the current booking phase: **modify** → `symbolField`; **new** →
 * `templateSymbolField`. Single implementation shared with `createManualDealSymbolGuards` and
 * `symbolStringForValidation` in `manualDealValidation.ts`.
 */
export function manualDealActiveSymbolString(
  form: {
    symbolField?: { value?: { Symbol?: string } };
    templateSymbolField?: { value?: { Symbol?: string } };
  },
  isModifying: boolean
): string | undefined {
  return securityForManualDealMode(
    form as { symbolField?: { value?: Security }; templateSymbolField?: { value?: Security } },
    isModifying
  )?.Symbol;
}

/** OTC forward: product template when booking new; instantiated forward security when modifying. */
export function isManualDealForwardSymbol(
  symbol: Security | undefined,
  isModifying: boolean
): symbol is OTCForwardSecurity {
  return isModifying ? isInstantiatedOTCForwardSecurity(symbol) : isOTCForwardProductTemplate(symbol);
}

/** Fixed-rate loan: product template when booking new; loan leg security when modifying. */
export function isManualDealFixedRateLoanSymbol(
  symbol: Security | undefined,
  isModifying: boolean
): symbol is OTCFixedRateLoanSecurity {
  return isModifying ? isInstantiatedFixedRateLoanSecurity(symbol) : isFixedRateLoanProductTemplate(symbol);
}

/** Floating-rate loan: product template when booking new; loan leg security when modifying. */
export function isManualDealFloatingRateLoanSymbol(
  symbol: Security | undefined,
  isModifying: boolean
): symbol is OTCFloatingRateLoanSecurity {
  return isModifying ? isInstantiatedFloatingRateLoanSecurity(symbol) : isFloatingRateLoanProductTemplate(symbol);
}

/** Vanilla OTC option: product template when booking new; option security when modifying. */
export function isManualDealOptionSymbol(
  symbol: Security | undefined,
  isModifying: boolean
): symbol is OTCOptionSecurity {
  return isModifying ? isInstantiatedOTCOptionSecurity(symbol) : isOTCOptionProductTemplate(symbol);
}

/** OTC repo: product template when booking new; repo security when modifying. */
export function isManualDealRepoSymbol(symbol: Security | undefined, isModifying: boolean): symbol is OTCRepoSecurity {
  return isModifying ? isInstantiatedOTCRepoSecurity(symbol) : isOTCRepoProductTemplate(symbol);
}

/**
 * Bound helpers for one booking phase (new vs modify). Use at hook / command / selector entry
 * to avoid threading `isModifying` through every `isManualDeal*Symbol` / `securityForManualDealMode` call.
 */
export interface ManualDealSymbolGuards {
  readonly isModifying: boolean;
  /**
   * The {@link Security} that currently drives the manual deal form: **modify** uses the deal’s
   * instrument (`symbolField`); **new** booking uses the selected product template (`templateSymbolField`).
   *
   * @see {@link securityForManualDealMode} — same logic with an explicit `isModifying` flag.
   */
  securityForMode(form: {
    symbolField?: { value?: Security };
    templateSymbolField?: { value?: Security };
  }): Security | undefined;
  isForward(symbol: Security | undefined): symbol is OTCForwardSecurity;
  isFixedRateLoan(symbol: Security | undefined): symbol is OTCFixedRateLoanSecurity;
  isFloatingRateLoan(symbol: Security | undefined): symbol is OTCFloatingRateLoanSecurity;
  isOption(symbol: Security | undefined): symbol is OTCOptionSecurity;
  isRepo(symbol: Security | undefined): symbol is OTCRepoSecurity;
  /** Symbol string from the active field; delegates to {@link manualDealActiveSymbolString}. */
  activeSymbolString(form: {
    symbolField?: { value?: { Symbol?: string } };
    templateSymbolField?: { value?: { Symbol?: string } };
  }): string | undefined;
}

/**
 * Factory for `ManualDealSymbolGuards` instance. Creates a new instance with the given `isModifying` flag
 * to encapsulate the current booking phase.
 *
 * @param isModifying - `true` when modifying an existing deal; `false` when booking a new deal.
 * @returns A `ManualDealSymbolGuards` instance with the given `isModifying` flag.
 */
export function createManualDealSymbolGuards(isModifying: boolean): ManualDealSymbolGuards {
  return {
    isModifying,
    securityForMode: form => securityForManualDealMode(form, isModifying),
    isForward: symbol => isManualDealForwardSymbol(symbol, isModifying),
    isFixedRateLoan: symbol => isManualDealFixedRateLoanSymbol(symbol, isModifying),
    isFloatingRateLoan: symbol => isManualDealFloatingRateLoanSymbol(symbol, isModifying),
    isOption: symbol => isManualDealOptionSymbol(symbol, isModifying),
    isRepo: symbol => isManualDealRepoSymbol(symbol, isModifying),
    activeSymbolString: form => manualDealActiveSymbolString(form, isModifying),
  };
}
